<template>
  <ApexChart v-if="chartData && chartData.series" element="RpmFuelSpeed" :chartOption="chartData"
    :series="chartData.series" />
</template>

<script>
import moment from 'moment';

export default {
  name: 'RpmFuelSpeed',
  props: ['series'],
  computed: {
    chartData() {
      if (!this.series) return null;
      const isMobile = window.innerWidth <= 991;
      // console.log('this.series >>> \n', JSON.stringify(this.series, null, 2));

      // 1. Ekstraksi data
      const MappedSpeed = this.series.map((data) => {
        const v = (data && data.gpsData && Number.isFinite(Number(data.gpsData.speed)))
          ? Number(data.gpsData.speed) * 0.5399568034557236
          : null;
        return Number.isFinite(v) ? Number(v.toFixed(2)) : null;
      });
      const MappedPortInFlow = this.series.map((data) => {
        const raw = data && data.fmData && Number.isFinite(Number(data.fmData.PORT_InFlow))
          ? Number(data.fmData.PORT_InFlow) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });
      const MappedStarboardInFlow = this.series.map((data) => {
        const raw = data && data.fmData && Number.isFinite(Number(data.fmData.STARBOARD_InFlow))
          ? Number(data.fmData.STARBOARD_InFlow) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });
      const MappedPortRpm = this.series.map((data) => {
        const raw = data && data.rpmData && data.rpmData.PORT && Number.isFinite(Number(data.rpmData.PORT.rpm))
          ? Number(data.rpmData.PORT.rpm) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });
      const MappedStarboardRpm = this.series.map((data) => {
        const raw = data && data.rpmData && data.rpmData.STARBOARD && Number.isFinite(Number(data.rpmData.STARBOARD.rpm))
          ? Number(data.rpmData.STARBOARD.rpm) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });
      const MappedPortRunningHour = this.series.map((data) => {
        const raw = data && data.rpmData && data.rpmData.PORT && Number.isFinite(Number(data.rpmData.PORT.runningTime))
          ? Number(data.rpmData.PORT.runningTime) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });
      const MappedStarboardRunningHour = this.series.map((data) => {
        const raw = data && data.rpmData && data.rpmData.STARBOARD && Number.isFinite(Number(data.rpmData.STARBOARD.runningTime))
          ? Number(data.rpmData.STARBOARD.runningTime) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });

      // Data Exhaust Temperature
      const MappedPortExhaustTemp = this.series.map((data) => {
        const raw = data && data.tempData && data.tempData.EXHAUST_TEMPERATURE && data.tempData.EXHAUST_TEMPERATURE.PORT && data.tempData.EXHAUST_TEMPERATURE.PORT[0] && Number.isFinite(Number(data.tempData.EXHAUST_TEMPERATURE.PORT[0].temperature))
          ? Number(data.tempData.EXHAUST_TEMPERATURE.PORT[0].temperature) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });
      const MappedStarboardExhaustTemp = this.series.map((data) => {
        const raw = data && data.tempData && data.tempData.EXHAUST_TEMPERATURE && data.tempData.EXHAUST_TEMPERATURE.STARBOARD && data.tempData.EXHAUST_TEMPERATURE.STARBOARD[0] && Number.isFinite(Number(data.tempData.EXHAUST_TEMPERATURE.STARBOARD[0].temperature))
          ? Number(data.tempData.EXHAUST_TEMPERATURE.STARBOARD[0].temperature) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });

      // 2. Terapkan interpolasi
      const InterpolatedSpeed = this.interpolateNulls(MappedSpeed);
      const InterpolatedPortInFlow = this.interpolateNulls(MappedPortInFlow);
      const InterpolatedStarboardInFlow = this.interpolateNulls(MappedStarboardInFlow);
      const InterpolatedPortRpm = this.interpolateNulls(MappedPortRpm);
      const InterpolatedStarboardRpm = this.interpolateNulls(MappedStarboardRpm);
      const InterpolatedPortRunningHour = this.interpolateNulls(MappedPortRunningHour);
      const InterpolatedStarboardRunningHour = this.interpolateNulls(MappedStarboardRunningHour);

      // Interpolasi Exhaust Temperature
      const InterpolatedPortExhaustTemp = this.interpolateNulls(MappedPortExhaustTemp);
      const InterpolatedStarboardExhaustTemp = this.interpolateNulls(MappedStarboardExhaustTemp);

      const safeMax = (arr, fallback = 10) => {
        const vals = arr.map((v) => (typeof v === 'string' ? Number(v) : v)).filter((v) => Number.isFinite(v));
        return vals.length ? Math.max(...vals) : fallback;
      };

      let MaxSpeed = safeMax(InterpolatedSpeed);
      MaxSpeed += (MaxSpeed * 0.10);

      let MaxInFlow = safeMax([...InterpolatedPortInFlow, ...InterpolatedStarboardInFlow]);
      MaxInFlow += (MaxInFlow * 0.10);

      let MaxRpm = safeMax([...InterpolatedPortRpm, ...InterpolatedStarboardRpm], 1000);
      MaxRpm = (Math.ceil(MaxRpm / 100) * 100) + 100;

      // Max untuk Exhaust Temperature
      let MaxTemp = safeMax([...InterpolatedPortExhaustTemp, ...InterpolatedStarboardExhaustTemp], 100);
      MaxTemp += (MaxTemp * 0.10);

      const seriesNames = {
        speed: 'Speed (Knot)',
        portFlow: 'Port Flow (L/h)',
        starboardFlow: 'Starboard Flow (L/h)',
        portRpm: 'Port RPM',
        starboardRpm: 'Starboard RPM',
        portRh: 'Port Running Time (seconds)',
        starboardRh: 'Starboard Running Time (seconds)',
        // Nama seri Exhaust Temperature
        portExhaustTemp: 'Port Exhaust Temp (°C)',
        starboardExhaustTemp: 'Starboard Exhaust Temp (°C)',
      };

      const seriesData = [
        // Speed
        { name: seriesNames.speed, data: MappedSpeed, color: '#0084ff' },
        { name: `${seriesNames.speed} (Interpolated)`, data: InterpolatedSpeed, color: 'rgba(0, 132, 255, 0.4)' },
        // Port Flow
        { name: seriesNames.portFlow, data: MappedPortInFlow, color: '#8B5CF6' },
        { name: `${seriesNames.portFlow} (Interpolated)`, data: InterpolatedPortInFlow, color: 'rgba(139, 92, 246, 0.4)' },
        // Starboard Flow
        { name: seriesNames.starboardFlow, data: MappedStarboardInFlow, color: '#10B981' },
        { name: `${seriesNames.starboardFlow} (Interpolated)`, data: InterpolatedStarboardInFlow, color: 'rgba(16, 185, 129, 0.4)' },
        // Port RPM
        { name: seriesNames.portRpm, data: MappedPortRpm, color: '#EF4444' },
        { name: `${seriesNames.portRpm} (Interpolated)`, data: InterpolatedPortRpm, color: 'rgba(239, 68, 68, 0.4)' },
        // Starboard RPM
        { name: seriesNames.starboardRpm, data: MappedStarboardRpm, color: '#F97316' },
        { name: `${seriesNames.starboardRpm} (Interpolated)`, data: InterpolatedStarboardRpm, color: 'rgba(249, 115, 22, 0.4)' },
        // Port Running Time
        { name: seriesNames.portRh, data: MappedPortRunningHour, color: '#E91E63' },
        { name: `${seriesNames.portRh} (Interpolated)`, data: InterpolatedPortRunningHour, color: 'rgba(233, 30, 99, 0.4)' },
        // Starboard Running Time
        { name: seriesNames.starboardRh, data: MappedStarboardRunningHour, color: '#00BCD4' },
        { name: `${seriesNames.starboardRh} (Interpolated)`, data: InterpolatedStarboardRunningHour, color: 'rgba(0, 188, 212, 0.4)' },
        // Seri data Exhaust Temperature
        // Port Exhaust Temp
        { name: seriesNames.portExhaustTemp, data: MappedPortExhaustTemp, color: '#F59E0B' }, // Amber
        { name: `${seriesNames.portExhaustTemp} (Interpolated)`, data: InterpolatedPortExhaustTemp, color: 'rgba(245, 158, 11, 0.4)' },
        // Starboard Exhaust Temp
        { name: seriesNames.starboardExhaustTemp, data: MappedStarboardExhaustTemp, color: '#14B8A6' }, // Teal
        { name: `${seriesNames.starboardExhaustTemp} (Interpolated)`, data: InterpolatedStarboardExhaustTemp, color: 'rgba(20, 184, 166, 0.4)' },
      ];

      const legendFillColors = seriesData.map((s) => (s.name.includes('(Interpolated)') ? 'transparent' : s.color));

      return {
        connectNulls: true,
        series: seriesData,
        chart: {
          height: 600,
          width: '100%',
          type: 'line',
          stacked: false,
          animations: {
            enabled: false,
          },
          events: {
            legendClick(chartContext, seriesIndex) {
              const seriesName = chartContext.w.globals.seriesNames[seriesIndex];
              const interpolatedName = `${seriesName} (Interpolated)`;
              chartContext.toggleSeries(interpolatedName);
            },
          },
          zoom: {
            enabled: true,
            allowMouseWheelZoom: false,
          },
        },
        markers: {
          size: 4,
        },
        stroke: {
          // 18 series (9 data * 2)
          width: [3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2],
          curve: 'smooth',
          dashArray: [0, 8, 0, 8, 0, 8, 0, 8, 0, 8, 0, 8, 0, 8, 0, 8, 0, 8],
        },
        legend: {
          position: 'bottom',
          formatter(seriesName) {
            if (seriesName.includes('(Interpolated)')) {
              return '';
            }
            return seriesName;
          },
          markers: {
            fillColors: legendFillColors,
          },
        },
        tooltip: {
          shared: true,
          custom({
            // eslint-disable-next-line no-unused-vars
            series, seriesIndex, dataPointIndex, w,
          }) {
            const header = w.globals.categoryLabels[dataPointIndex];
            let tooltipContent = '';

            for (let i = 0; i < series.length; i++) {
              if (!w.globals.seriesNames[i].includes('(Interpolated)')) {
                const seriesValue = series[i][dataPointIndex];
                if (seriesValue !== null && typeof seriesValue !== 'undefined') {
                  const seriesColor = w.globals.colors[i];
                  const seriesName = w.globals.seriesNames[i];
                  tooltipContent += `
                    <div class="apexcharts-tooltip-series-group" style="order: ${i}; display: flex;">
                      <span class="apexcharts-tooltip-marker" style="background-color: ${seriesColor};"></span>
                      <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                        <div class="apexcharts-tooltip-y-group">
                          <span class="apexcharts-tooltip-text-y-label">${seriesName}: </span>
                          <span class="apexcharts-tooltip-text-y-value">${seriesValue}</span>
                        </div>
                      </div>
                    </div>`;
                }
              }
            }

            if (tooltipContent === '') {
              return '';
            }

            return `
              <div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                ${header}
              </div>
              <div class="apexcharts-tooltip-series-group-wrapper" style="display: flex; flex-direction: column;">
                ${tooltipContent}
              </div>`;
          },
        },
        xaxis: {
          type: 'category',
          labels: {
            formatter(val) {
              return moment(val).format('DD-MM-YYYY HH:mm');
            },
          },
          categories: this.series.map((data) => moment(data.timestamp * 1000).format('DD MMMM YYYY HH:mm')),
          tickAmount: isMobile ? 3 : undefined,
        },
        yaxis: [
          // Speed Axis
          { max: MaxSpeed, title: { text: 'Speed (Knot)' }, show: !isMobile },
          { max: MaxSpeed, show: false },
          // Port Flow Axis
          { max: MaxInFlow, title: { text: 'Port Flow (L/h)' }, show: !isMobile },
          { max: MaxInFlow, show: false },
          // Starboard Flow Axis
          { max: MaxInFlow, title: { text: 'Stbd. Flow (L/h)' }, show: !isMobile },
          { max: MaxInFlow, show: false },
          // Port RPM Axis
          {
            opposite: true, max: MaxRpm, title: { text: 'Port RPM' }, show: !isMobile,
          },
          { opposite: true, max: MaxRpm, show: false },
          // Starboard RPM Axis
          {
            opposite: true, max: MaxRpm, title: { text: 'Stbd. RPM' }, show: !isMobile,
          },
          { opposite: true, max: MaxRpm, show: false },
          // Port Running Time Axis
          {
            opposite: true, title: { text: 'Port Run. Time (seconds)' }, show: !isMobile,
          },
          { opposite: true, show: false },
          // Starboard Running Time Axis
          {
            opposite: true, title: { text: 'Stbd. Run. Time (seconds)' }, show: !isMobile,
          },
          { opposite: true, show: false },

          // Sumbu Y Exhaust Temperature
          // Port Exhaust Temp Axis
          {
            opposite: true, max: MaxTemp, title: { text: 'Port Exh. Temp (°C)' }, show: !isMobile,
          },
          { opposite: true, max: MaxTemp, show: false },
          // Starboard Exhaust Temp Axis
          {
            opposite: true, max: MaxTemp, title: { text: 'Stbd. Exh. Temp (°C)' }, show: !isMobile,
          },
          { opposite: true, max: MaxTemp, show: false },
        ],
      };
    },
  },
  methods: {
    interpolateNulls(dataArray) {
      const result = [...dataArray];
      for (let i = 0; i < result.length; i++) {
        if (result[i] === null) {
          const startIndex = i - 1;
          const startValue = startIndex >= 0 ? result[startIndex] : null;

          if (startValue !== null) {
            let endIndex = -1;
            for (let j = i + 1; j < result.length; j++) {
              if (result[j] !== null) {
                endIndex = j;
                break;
              }
            }

            if (endIndex !== -1) {
              const endValue = result[endIndex];
              const gapCount = endIndex - startIndex;
              const totalDifference = endValue - startValue;
              const step = totalDifference / gapCount;

              for (let k = startIndex + 1; k < endIndex; k++) {
                const interpolatedValue = startValue + step * (k - startIndex);
                result[k] = Number(interpolatedValue.toFixed(2));
              }
              i = endIndex - 1;
            }
          }
        }
      }
      return result;
    },
  },
};
</script>

<style scoped></style>
